import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Megaphone, ArrowLeft, Save, Users, BookOpen, Send } from 'lucide-react';
import { toast } from 'sonner';

const SendAnnouncementPage = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    target_type: 'all', // all, course
    course_id: '',
    priority: 'normal' // normal, important, urgent
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await api.get('/courses');
      setCourses(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des cours');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.content) {
      toast.error('Veuillez remplir le titre et le contenu');
      return;
    }

    if (formData.target_type === 'course' && !formData.course_id) {
      toast.error('Veuillez sélectionner un cours');
      return;
    }

    setLoading(true);
    try {
      // Backend expects multipart/form-data (Form fields), not JSON.
      const payload = new FormData();
      payload.append('title', formData.title);
      payload.append('content', formData.content);
      payload.append('priority', formData.priority);
      if (formData.target_type === 'course' && formData.course_id) {
        payload.append('target_course_id', formData.course_id);
      } else {
        payload.append('target_role', 'student');
      }

      await api.post('/announcements', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Annonce envoyée ! Les étudiants concernés ont été notifiés.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error sending announcement:', error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d?.msg).filter(Boolean).join(', ')
          : "Erreur lors de l'envoi de l'annonce";
      toast.error(msg || "Erreur lors de l'envoi de l'annonce");
    } finally {
      setLoading(false);
    }
  };

  const priorityOptions = [
    { value: 'normal', label: 'Normal', color: 'bg-slate-100 text-slate-700' },
    { value: 'important', label: 'Important', color: 'bg-amber-100 text-amber-700' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Envoyer une Annonce" onMenuClick={onMenuClick} />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate('/dashboard')}
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour au tableau de bord
        </Button>

        <form onSubmit={handleSubmit}>
          {/* Target Audience */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-600" />
                Destinataires
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => handleInputChange('target_type', 'all')}
                  className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                    formData.target_type === 'all'
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-amber-300'
                  }`}
                >
                  <Users className={`w-8 h-8 mx-auto mb-2 ${formData.target_type === 'all' ? 'text-amber-600' : 'text-slate-400'}`} />
                  <p className="font-medium text-slate-900 dark:text-white">Tous mes étudiants</p>
                  <p className="text-xs text-slate-500 mt-1">Envoyer à tous les étudiants de vos cours</p>
                </button>
                
                <button
                  type="button"
                  onClick={() => handleInputChange('target_type', 'course')}
                  className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                    formData.target_type === 'course'
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-amber-300'
                  }`}
                >
                  <BookOpen className={`w-8 h-8 mx-auto mb-2 ${formData.target_type === 'course' ? 'text-amber-600' : 'text-slate-400'}`} />
                  <p className="font-medium text-slate-900 dark:text-white">Un cours spécifique</p>
                  <p className="text-xs text-slate-500 mt-1">Cibler les étudiants d'un cours</p>
                </button>
              </div>

              {formData.target_type === 'course' && (
                <div className="mt-4">
                  <Label className="text-sm font-medium mb-2 block">Sélectionner le cours</Label>
                  <Select value={formData.course_id} onValueChange={(v) => handleInputChange('course_id', v)}>
                    <SelectTrigger data-testid="select-course">
                      <SelectValue placeholder="Choisir un cours" />
                    </SelectTrigger>
                    <SelectContent>
                      {courses.map(course => (
                        <SelectItem key={course.id} value={course.id.toString()}>
                          {course.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Announcement Content */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-amber-600" />
                Contenu de l'Annonce
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Titre *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Ex: Rappel - Rendu du projet demain"
                  data-testid="input-title"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2 block">Message *</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  placeholder="Rédigez votre message aux étudiants..."
                  rows={6}
                  data-testid="input-content"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Priorité</Label>
                <div className="flex gap-3">
                  {priorityOptions.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleInputChange('priority', option.value)}
                      className={`px-4 py-2 rounded-lg font-medium transition-all ${
                        formData.priority === option.value
                          ? `${option.color} ring-2 ring-offset-2 ring-amber-400`
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card className="mb-6 border-amber-200 dark:border-amber-800">
            <CardHeader className="bg-amber-50 dark:bg-amber-900/20">
              <CardTitle className="text-amber-700 dark:text-amber-400 text-sm">
                Aperçu de l'annonce
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="p-4 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <Megaphone className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900 dark:text-white">
                      {formData.title || 'Titre de l\'annonce'}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 whitespace-pre-wrap">
                      {formData.content || 'Le contenu de votre annonce apparaîtra ici...'}
                    </p>
                    <p className="text-xs text-slate-400 mt-2">
                      Maintenant • {formData.target_type === 'all' ? 'Tous les étudiants' : 'Cours sélectionné'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => navigate('/dashboard')}
            >
              Annuler
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="submit-announcement"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Envoi en cours...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Envoyer l'annonce
                </span>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default SendAnnouncementPage;
